import React, { useEffect, useState } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import {
  fetchForwardPoints,
  fetchForwardPointsForTimeStamp,
  sortForwardPointsByTenor,
  sortForwardTenors,
} from "../Utils/CoTraderUtils";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Boost from "highcharts/modules/boost";

interface CoTraderForwardChartProps {
  currencyPair: string;
  setTenorList: any;
  tenors: any;
  executionTimestamp?: string | null;
  chartHeight?: string | number;
  refreshIntervalMs?: number | null;
}

const CoTraderForwardChart: React.FunctionComponent<
  CoTraderForwardChartProps
> = (
  {
    currencyPair,
    setTenorList,
    tenors,
    executionTimestamp,
    chartHeight = "150px",
    refreshIntervalMs = 20000,
  }
) => {
  const [forwardPoints, setForwardPoints]: any = useState([]);
  const isHistorical = Boolean(executionTimestamp);

  const chartOptions = ForwardChartOptions({
    forwardPoints,
    tenors,
    chartHeight,
  });

  useEffect(() => {
    const updatePrices = () => {
      if (isHistorical) {
        return fetchForwardPointsForTimeStamp({
          setForwardPoints,
          symbol: currencyPair,
          setTenorList,
          executionTimestamp,
        });
      }

      return fetchForwardPoints({
        setForwardPoints,
        symbol: currencyPair,
        setTenorList,
      });
    };

    void updatePrices();
    if (isHistorical || !refreshIntervalMs) return undefined;

    const intervalId = window.setInterval(() => {
      void updatePrices();
    }, refreshIntervalMs);

    return () => clearInterval(intervalId);
  }, [
    currencyPair,
    executionTimestamp,
    isHistorical,
    refreshIntervalMs,
    setTenorList,
  ]);

  const useStyles = makeStyles({
    container: {
      width: "100%",
      height: "100%",
      // padding: "10px",
    },
    chartContainer: {
      overflow: "auto",
      height: "100%",
      width: "100%",
      backgroundColor: "",
      // padding: "10px",
    },
  });

  const classes = useStyles();
  Boost(Highcharts);

  return (
    <>
      <div className={classes.container}>
        <div className={classes.chartContainer}>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>
      </div>
    </>
  );
};

export default CoTraderForwardChart;

const FWD_DIVISOR = 10000;

const ForwardChartOptions = ({ forwardPoints, tenors, chartHeight }: any) => {
  const theme = useTheme();
  const allowedTenors = sortForwardTenors(tenors || []);
  const filteredForwardPoints = sortForwardPointsByTenor(forwardPoints || []).filter(
    (item: any) => allowedTenors.includes(item.tenor)
  );

  const safeYAxisValues = filteredForwardPoints
    .flatMap((item: any) => [
      parseFloat(item.forward_points_bid) / FWD_DIVISOR,
      parseFloat(item.forward_points_ask) / FWD_DIVISOR,
    ])
    .filter((value: number) => Number.isFinite(value));

  const yAxisMin =
    safeYAxisValues.length > 0 ? Math.min(...safeYAxisValues) : undefined;
  const yAxisMax =
    safeYAxisValues.length > 0 ? Math.max(...safeYAxisValues) : undefined;

  return {
    chart: {
      type: "column",
      height: chartHeight,
      zoomType: "x",
      backgroundColor: theme.globallink.colors.DarkGrey[600],
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: filteredForwardPoints.map((item: any) => item.tenor),
      title: {
        text: "Tenor",
      },
      labels: {
        style: {
          color: theme.globallink.colors.Grey[500],
        },
      },
    },
    yAxis: {
      title: {
        text: "Forward Points",
      },
      style: {
        color: theme.globallink.colors.Grey[500],
      },
      labels: {
        style: {
          color: theme.globallink.colors.Grey[500],
        },
      },
      min: yAxisMin, // Dynamic min value
      max: yAxisMax, // Optional: Ensure a bounded range
      gridLineWidth: 0, // Removes grid lines
      plotLines: [
        {
          value: 0, // Line at 0
          color: "white", // Optional: White line
          width: 1, // Thickness
        },
      ],
    },
    legend: {
      enabled: false,
      layout: "horizontal",
      align: "center",
      verticalAlign: "top",
    },
    tooltip: {
      shared: true,
      useHTML: true,
      style: {
        color: theme.globallink.colors.Grey[500],
      },

      backgroundColor: theme.globallink.colors.DarkGrey[500],
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const index = this.points ? this.points[0].point.index : 0;
        const bid = parseFloat(filteredForwardPoints[index].forward_points_bid) / FWD_DIVISOR;
        const ask = parseFloat(filteredForwardPoints[index].forward_points_ask) / FWD_DIVISOR;
        const midpoint = (bid + ask) / 2;

        return `
          <b>Tenor: ${this.x}</b><br/>
          <span style="color:#dc3545">●</span> Bid: <b>${bid.toFixed(
            4
          )}</b><br/>
          <span style="color:#007bff">●</span> Mid: <b>${midpoint.toFixed(
            4
          )}</b><br/>
          <span style="color:#28a745">●</span> Ask: <b>${ask.toFixed(
            4
          )}</b><br/>
        `;
      },
    },
    series: [
      {
        type: "column",
        name: "Bid",
        data: filteredForwardPoints.map((item: any) =>
          parseFloat(item.forward_points_bid) / FWD_DIVISOR
        ),
        color: "#D6111C",
        borderWidth: 0,
        borderColor: null,
        borderRadius: 0, // Ensures flat bars
        opacity: 0.9,
      },
      {
        type: "column",
        name: "Mid",
        data: filteredForwardPoints.map(
          (item: any) =>
            (parseFloat(item.forward_points_bid) / FWD_DIVISOR +
              parseFloat(item.forward_points_ask) / FWD_DIVISOR) /
            2
        ),
        color: theme.globallink.colors.Blue[300],
        borderWidth: 0,
        borderColor: null,
        borderRadius: 0, // Ensures flat bars
        opacity: 0.9,
      },
      {
        type: "column",
        name: "Ask",
        data: filteredForwardPoints.map((item: any) =>
          parseFloat(item.forward_points_ask) / FWD_DIVISOR
        ),
        color: "#168547",
        borderWidth: 0,
        borderColor: null,
        borderRadius: 0, // Ensures flat bars
        opacity: 0.9,
      },
      {
        type: "line",
        name: "Mid",
        data: filteredForwardPoints.map(
          (item: any) =>
            (parseFloat(item.forward_points_bid) / FWD_DIVISOR +
              parseFloat(item.forward_points_ask) / FWD_DIVISOR) /
            2
        ),
        color: theme.globallink.colors.Blue[300],
      },
    ],
    credits: {
      enabled: false,
    },
  };
};
